import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { SubscribeDialog } from "@/components/site/SubscribeDialog";
import { HowItWorks, MailGallery, PhotoGallery, PlanFacts, Firefly } from "@/components/plan/PlanSections";
import { PLAN_HERO_URL } from "@/lib/brand";
import { PLANS, billingLine, envelopesLine, inr, planBySlug, usePlanAvailability } from "@/lib/plans";
import { usePageMeta } from "@/lib/meta";

const Hero = ({ plan }) => (
  <section className="relative overflow-hidden" data-testid="plan-hero">
    <div className="max-w-6xl mx-auto px-6 pt-14 pb-20 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-6">
        <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-amber-800 font-semibold" data-testid="plan-hero-pill"><Firefly /> {plan.recurring ? plan.durationLabel : "One-time"} · {inr(plan.price)}</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.1] text-stone-900 mt-6" data-testid="plan-hero-headline">A little happiness, delivered to your mailbox.</h1>
        <p className="text-base sm:text-lg text-stone-700 mt-6 leading-relaxed max-w-lg">An experience designed to bring back the joy of receiving something thoughtful, personal and made with love.</p>
        <a href="#subscribe" className="inline-block mt-10 rounded-full bg-stone-900 text-white px-7 py-3.5 hover:bg-amber-600 transition-colors duration-300" data-testid="plan-hero-subscribe-link">{plan.recurring ? `Subscribe to ${plan.durationLabel}` : "Get a single envelope"}</a>
      </div>
      <div className="lg:col-span-6 relative">
        <div className="lantern-glow absolute -inset-10 rounded-full" />
        <img src={PLAN_HERO_URL} alt="Opening a Little Lantern Club envelope" className="relative w-full aspect-[3/2] object-cover rounded-3xl border-8 border-white shadow-sm" />
      </div>
    </div>
  </section>
);

const PlanSwitcher = ({ current }) => (
  <div className="flex flex-wrap gap-2 justify-center mt-10" data-testid="plan-switcher">
    {PLANS.map((p) => (
      <Link key={p.id} to={`/plans/${p.slug}`} className={`rounded-full px-4 py-2 text-sm border transition-colors ${p.id === current.id ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-300 hover:border-amber-500"}`} data-testid={`plan-switch-${p.id}`}>
        {p.recurring ? p.durationLabel : "One-time"} · {inr(p.price)}
      </Link>
    ))}
  </div>
);

const FinalCta = ({ plan, available, onSubscribe }) => (
  <section id="subscribe" className="py-28 bg-[#FFFBEB] border-t border-amber-200" data-testid="plan-final-cta">
    <div className="max-w-3xl mx-auto px-6 text-center">
      <Firefly className="mb-6" />
      <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-stone-900">{plan.name} · {inr(plan.price)}</h2>
      <p className="text-stone-600 mt-4">{billingLine(plan)} · {envelopesLine(plan)} · free shipping in India</p>
      {available ? (
        <button onClick={onSubscribe} className="mt-10 rounded-full bg-stone-900 text-white px-10 py-4 text-lg hover:bg-amber-600 transition-colors duration-300" data-testid="plan-subscribe-now-button">Subscribe Now</button>
      ) : (
        <div className="mt-10" data-testid="plan-coming-soon">
          <button disabled className="rounded-full bg-stone-200 text-stone-500 px-10 py-4 text-lg cursor-not-allowed" data-testid="plan-subscribe-now-button">{available === null ? "Loading…" : "Coming soon"}</button>
          {available === false && <p className="text-sm text-stone-600 mt-4">This plan opens for subscriptions very soon. The other plans below may already be open.</p>}
        </div>
      )}
      <PlanSwitcher current={plan} />
    </div>
  </section>
);

export default function PlanDetails() {
  const { slug } = useParams();
  const plan = planBySlug(slug);
  const availability = usePlanAvailability();
  const [open, setOpen] = useState(false);
  usePageMeta(plan ? `${plan.name} subscription (${inr(plan.price)}) · The Little Lantern Club` : null, plan ? `/plans/${plan.slug}` : null);

  if (!plan) return <Navigate to="/#pricing" replace />;
  const available = availability ? !!availability[plan.id] : null;
  return (
    <div className="bg-white paper-grain min-h-screen" data-testid={`plan-page-${plan.id}`}>
      <Nav />
      <main>
        <Hero plan={plan} />
        <MailGallery />
        <HowItWorks />
        <PlanFacts plan={plan} />
        <PhotoGallery />
        <FinalCta plan={plan} available={available} onSubscribe={() => setOpen(true)} />
      </main>
      <Footer />
      <SubscribeDialog open={open} onOpenChange={setOpen} plan={plan} />
    </div>
  );
}
